import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import { AuthService } from 'src/app/features/services/auth/auth.service';
import { environment } from 'src/app/environments/environments';

const WIDGET_TAG_ID = 'conversell-support-assistant';
const SCRIPT_ID = 'conversell-support-assistant-script';
const STYLE_ID = 'conversell-support-assistant-style';

/** Preview Widget: /design/:id/simulation/:type */
const PREVIEW_WIDGET_ROUTE = /\/design\/[^/]+\/simulation(\/|$)/;

/** Responses editor — widget CSS remounts Material tabs; keep widget off this screen. */
const RESPONSES_ROUTE = /\/design\/[^/]+\/fulfillment-response(\/|$)/;

/** Super Admin console */
const SUPER_ADMIN_ROUTE = /^\/admin(\/|$)/;

/** Public / auth screens — never show the bot on any /auth/* page (login, signup, forget-password, action/reset, ...) */
const AUTH_OR_PUBLIC_ROUTES =
  /^\/auth(\/|$)|^\/(login|signup|signup-details|forget-password|email-verification)(\/|$)|^\/accept-invitation(\/|$)/;

@Injectable({ providedIn: 'root' })
export class SupportAssistantWidgetService implements OnDestroy {
  private visible = false;
  private sub?: Subscription;

  private readonly chatbotId =
    (environment as any).supportAssistantChatbotId ||
    '93e6c1d5-0c7f-402b-8226-0d468d0a1c2d';

  private readonly origin =
    (environment as any).supportAssistantIframeOrigin ||
    'https://widgetiframeconversell.firebaseapp.com';

  constructor(
    private router: Router,
    private auth: AuthService,
  ) {}

  init(): void {
    const url$ = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects || e.url),
      startWith(this.router.url),
      distinctUntilChanged(),
    );

    const userKey$ = this.auth.currentUser$.pipe(
      map((u) => (u ? `${u.id}|${u.role || ''}` : '')),
      distinctUntilChanged(),
    );

    this.sub = combineLatest([
      url$,
      this.auth.isAuthenticated$.pipe(distinctUntilChanged()),
      userKey$,
      this.auth.checkIfAdminAsUser().pipe(startWith(false), distinctUntilChanged()),
    ])
      .pipe(
        map(([url, isAuthenticated, userKey, isAdminAsUser]) =>
          this.shouldShow(url, !!isAuthenticated, userKey, !!isAdminAsUser),
        ),
        distinctUntilChanged(),
      )
      .subscribe((show) => {
        if (show) {
          this.ensureAssets();
          this.mount();
        } else {
          this.unmount();
        }
      });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.unmount();
  }

  private shouldShow(
    url: string,
    isAuthenticated: boolean,
    userKey: string,
    isAdminAsUser: boolean,
  ): boolean {
    const path = (url || '').split('?')[0];

    if (!isAuthenticated || !userKey) {
      return false;
    }
    if (AUTH_OR_PUBLIC_ROUTES.test(path)) {
      return false;
    }
    if (PREVIEW_WIDGET_ROUTE.test(path)) {
      return false;
    }
    if (RESPONSES_ROUTE.test(path)) {
      return false;
    }
    // Super Admin console — never (even during Connect to leftovers)
    if (SUPER_ADMIN_ROUTE.test(path)) {
      return false;
    }

    const role = (userKey.split('|')[1] || '').trim();
    const connectedAs =
      isAdminAsUser || this.auth.hasConnectAsSession();

    // SuperAdmin: only after Connect to, and only in the client workspace UI
    if (role === 'SuperAdmin' || this.auth.isSuperAdmin()) {
      return connectedAs;
    }

    // Workspace: admin or manager
    if (
      role === 'admin' ||
      role === 'manager' ||
      this.auth.isAdmin() ||
      this.auth.isManager()
    ) {
      return true;
    }

    return false;
  }

  private ensureAssets(): void {
    if (typeof document === 'undefined') {
      return;
    }

    if (!document.getElementById(STYLE_ID)) {
      const link = document.createElement('link');
      link.id = STYLE_ID;
      link.rel = 'stylesheet';
      link.href = `${this.origin}/styles.css`;
      document.head.appendChild(link);
    }

    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = `${this.origin}/index.js`;
      script.async = true;
      document.body.appendChild(script);
    }
  }

  private mount(): void {
    if (typeof document === 'undefined' || this.visible) {
      return;
    }
    if (!document.getElementById(WIDGET_TAG_ID)) {
      const el = document.createElement('chat-widget');
      el.id = WIDGET_TAG_ID;
      el.setAttribute('chatbotid', this.chatbotId);
      el.setAttribute('mode', 'widget');
      document.body.appendChild(el);
    }
    this.visible = true;
  }

  private unmount(): void {
    if (typeof document === 'undefined') {
      return;
    }
    document.getElementById(WIDGET_TAG_ID)?.remove();
    // Drop widget CSS so Responses tabs are not remounted / blinked
    document.getElementById(STYLE_ID)?.remove();
    this.visible = false;
  }
}
